import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import {
  BarChart3, MessageCircle, AlertTriangle, Zap, Clock, RefreshCw, Search,
  Flame,
} from "lucide-react";

/* Tiny helper to compute a percentage width from count vs max. */
function pct(count, max) {
  if (!max) return 0;
  return Math.max(2, Math.round((count / max) * 100));
}

/* ── SUMMARY TILES ─────────────────────────────────────────────── */
function Tile({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className={`fa-tile fa-accent-${accent}`} data-testid={testId}>
      <div className="fa-tile-head">
        <Icon size={14} />
        <span>{label}</span>
      </div>
      <div className="fa-tile-value">{value}</div>
    </div>
  );
}

/* ── HOT PILLS THIS WEEK (last 7 days) ────────────────────────── */
function HotPillsWidget({ rows }) {
  if (!rows?.length) {
    return (
      <div className="fa-empty" data-testid="hot-pills-empty">
        No clicks in the last 7 days yet.
      </div>
    );
  }
  return (
    <div className="fa-hot-list" data-testid="hot-pills-list">
      {rows.map((r, i) => (
        <div
          key={r.pill_id || `${r.pill_text}-${i}`}
          className="fa-hot-row"
          data-testid={`hot-pill-${i}`}
        >
          <div className="fa-hot-rank">{i + 1}</div>
          <div className="fa-hot-body">
            <div className="fa-hot-text">{r.pill_text}</div>
            <div className="fa-hot-meta">
              <span className="fa-chip">{r.issue_type || "—"}</span>
              {r.pill_id && <span className="fa-chip fa-chip-id">{r.pill_id}</span>}
            </div>
          </div>
          <div className="fa-hot-count" data-testid={`hot-pill-count-${i}`}>
            <Flame size={11} /> {r.count}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── TOP PILLS TABLE ───────────────────────────────────────────── */
function TopPillsTable({ rows }) {
  const max = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.count || 0), 0),
    [rows]
  );
  if (!rows.length) {
    return (
      <div className="fa-empty" data-testid="top-pills-empty">
        No pill clicks recorded yet.
      </div>
    );
  }
  return (
    <div className="fa-table" data-testid="top-pills-table">
      {rows.map((r, i) => (
        <div key={`${r.pill_id || r.pill_text}-${i}`} className="fa-row">
          <div className="fa-row-rank">#{i + 1}</div>
          <div className="fa-row-body">
            <div className="fa-row-text">{r.pill_text}</div>
            <div className="fa-row-meta">
              <span className="fa-chip">{r.issue_type || "—"}</span>
              {r.pill_id && <span className="fa-chip fa-chip-id">{r.pill_id}</span>}
              {r.last_clicked && (
                <span className="fa-chip fa-chip-time">
                  <Clock size={10} /> {new Date(r.last_clicked).toLocaleString()}
                </span>
              )}
            </div>
            <div className="fa-bar">
              <div className="fa-bar-fill" style={{ width: `${pct(r.count, max)}%` }} />
            </div>
          </div>
          <div className="fa-row-count">{r.count}</div>
        </div>
      ))}
    </div>
  );
}

/* ── BY-ISSUE / BY-MODE TABLES ─────────────────────────────────── */
function GroupTable({ rows, labelKey, emptyText, testId }) {
  const max = useMemo(
    () => rows.reduce((m, r) => Math.max(m, r.count || 0), 0),
    [rows]
  );
  if (!rows.length) {
    return <div className="fa-empty" data-testid={`${testId}-empty`}>{emptyText}</div>;
  }
  return (
    <div className="fa-group" data-testid={testId}>
      {rows.map((r, i) => (
        <div key={i} className="fa-group-row">
          <div className="fa-group-label">{r[labelKey] || "—"}</div>
          <div className="fa-group-bar">
            <div className="fa-group-bar-fill" style={{ width: `${pct(r.count, max)}%` }} />
          </div>
          <div className="fa-group-count">{r.count}</div>
        </div>
      ))}
    </div>
  );
}

/* ── DEAD PILLS LIST ───────────────────────────────────────────── */
function DeadPillsList({ rows, filter, setFilter }) {
  const filtered = useMemo(() => {
    if (!filter) return rows;
    const f = filter.toLowerCase();
    return rows.filter(
      (r) =>
        (r.pill_text || "").toLowerCase().includes(f) ||
        (r.issue_type || "").toLowerCase().includes(f) ||
        (r.pill_id || "").toLowerCase().includes(f)
    );
  }, [rows, filter]);

  return (
    <div className="fa-dead" data-testid="dead-pills-section">
      <div className="fa-dead-search">
        <Search size={13} />
        <input
          type="text"
          placeholder="Filter by issue, text, or id…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="dead-pills-filter"
        />
      </div>
      {filtered.length === 0 ? (
        <div className="fa-empty" data-testid="dead-pills-empty">
          {rows.length
            ? "No dead pills match your filter."
            : "No dead pills — every bank entry has at least one click."}
        </div>
      ) : (
        <div className="fa-dead-list">
          {filtered.map((r) => (
            <div key={r.pill_id} className="fa-dead-row">
              <div className="fa-dead-text">{r.pill_text}</div>
              <div className="fa-dead-meta">
                <span className="fa-chip">{r.issue_type}</span>
                <span className="fa-chip fa-chip-id">{r.pill_id}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── MAIN PAGE ─────────────────────────────────────────────────── */
export default function FollowUpAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [deadFilter, setDeadFilter] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/ask-stewie/follow-up-analytics`);
      setData(res.data);
    } catch (e) {
      setErr("Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const totals = data?.totals || { total_clicks: 0, unique_pills: 0, unique_users: 0 };
  const topIssue =
    data?.by_issue?.[0]?.issue_type ||
    (data?.by_issue?.length ? "—" : "—");
  const bankTotal = data?.bank_total_pills || 0;
  const deadCount = data?.dead_pills?.length || 0;

  return (
    <div className="fa-page" data-testid="follow-up-analytics-page">
      <style>{faStyles}</style>

      <div className="fa-header">
        <div>
          <h1 className="fa-title">Follow-Up Intelligence Analytics</h1>
          <p className="fa-sub">
            Which follow-up pills are actually getting clicked — and which ones aren't.
          </p>
        </div>
        <button
          type="button"
          className="fa-reload"
          onClick={load}
          data-testid="reload-analytics-btn"
          disabled={loading}
        >
          <RefreshCw size={13} className={loading ? "fa-spin" : ""} />
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="fa-error" data-testid="analytics-error">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {/* Summary tiles */}
      <div className="fa-tiles">
        <Tile
          icon={MessageCircle}
          label="TOTAL CLICKS"
          value={totals.total_clicks}
          accent="violet"
          testId="tile-total-clicks"
        />
        <Tile
          icon={Zap}
          label="UNIQUE PILLS CLICKED"
          value={`${totals.unique_pills} / ${bankTotal}`}
          accent="green"
          testId="tile-unique-pills"
        />
        <Tile
          icon={BarChart3}
          label="TOP ISSUE"
          value={topIssue}
          accent="cyan"
          testId="tile-top-issue"
        />
        <Tile
          icon={AlertTriangle}
          label="DEAD PILLS"
          value={`${deadCount} / ${bankTotal}`}
          accent="amber"
          testId="tile-dead-pills"
        />
      </div>

      {/* Hot pills (last 7 days) */}
      <section className="fa-section" data-testid="section-hot-pills">
        <h2 className="fa-section-title">
          <Flame size={14} style={{ color: "#fb923c", marginRight: 6, verticalAlign: "-2px" }} />
          Hot Pills This Week
        </h2>
        <p className="fa-section-hint">
          Top 5 pills ranked by clicks in the last 7 days — what stewards are asking right now.
        </p>
        <HotPillsWidget rows={data?.hot_pills_7d || []} />
      </section>

      {/* Top pills */}
      <section className="fa-section" data-testid="section-top-pills">
        <h2 className="fa-section-title">Top 20 Clicked Follow-Up Pills</h2>
        <TopPillsTable rows={data?.top_pills || []} />
      </section>

      {/* By issue + by mode */}
      <div className="fa-split">
        <section className="fa-section" data-testid="section-by-issue">
          <h2 className="fa-section-title">Clicks by Issue</h2>
          <GroupTable
            rows={data?.by_issue || []}
            labelKey="issue_type"
            emptyText="No clicks grouped by issue yet."
            testId="by-issue-table"
          />
        </section>
        <section className="fa-section" data-testid="section-by-mode">
          <h2 className="fa-section-title">Clicks by Response Mode</h2>
          <GroupTable
            rows={data?.by_mode || []}
            labelKey="response_mode"
            emptyText="No clicks grouped by mode yet."
            testId="by-mode-table"
          />
        </section>
      </div>

      {/* Dead pills */}
      <section className="fa-section" data-testid="section-dead-pills">
        <h2 className="fa-section-title">
          Dead Pills — bank entries with zero clicks
        </h2>
        <p className="fa-section-hint">
          Candidates for rewording, merging, or removing from the question bank.
        </p>
        <DeadPillsList
          rows={data?.dead_pills || []}
          filter={deadFilter}
          setFilter={setDeadFilter}
        />
      </section>

      {/* Recent events */}
      <section className="fa-section" data-testid="section-recent">
        <h2 className="fa-section-title">Recent Clicks</h2>
        {data?.recent?.length ? (
          <div className="fa-recent">
            {data.recent.map((r, i) => (
              <div key={i} className="fa-recent-row">
                <div className="fa-recent-time">
                  {r.created_at
                    ? new Date(r.created_at).toLocaleString()
                    : "—"}
                </div>
                <div className="fa-recent-body">
                  <div className="fa-recent-text">{r.pill_text}</div>
                  <div className="fa-recent-meta">
                    <span className="fa-chip">{r.issue_type || "—"}</span>
                    <span className="fa-chip">{r.response_mode || "—"}</span>
                    {r.pill_id && (
                      <span className="fa-chip fa-chip-id">{r.pill_id}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="fa-empty" data-testid="recent-empty">
            No recent clicks.
          </div>
        )}
      </section>
    </div>
  );
}

const faStyles = `
.fa-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 28px 20px 80px;
  color: #e4e4e7;
  font-family: 'Inter', -apple-system, sans-serif;
}

.fa-header {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 20px;
}
.fa-title {
  font-size: 22px; font-weight: 800; color: #fafafa; margin: 0 0 4px;
  letter-spacing: -0.01em;
}
.fa-sub { font-size: 13px; color: #a1a1aa; margin: 0; }

.fa-reload {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 8px;
  background: rgba(139,92,246,0.1);
  border: 1px solid rgba(139,92,246,0.25);
  color: #c4b5fd;
  font-size: 12px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.fa-reload:hover { background: rgba(139,92,246,0.18); border-color: rgba(139,92,246,0.45); }
.fa-reload:disabled { opacity: 0.6; cursor: default; }
.fa-spin { animation: fa-spin 0.9s linear infinite; }
@keyframes fa-spin { to { transform: rotate(360deg); } }

.fa-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; border-radius: 10px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.25);
  color: #fca5a5; font-size: 13px; font-weight: 600;
  margin-bottom: 18px;
}

/* Tiles */
.fa-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px; margin-bottom: 28px;
}
.fa-tile {
  padding: 16px 18px; border-radius: 14px;
  background: rgba(24,24,27,0.6);
  border: 1px solid rgba(255,255,255,0.06);
}
.fa-tile-head {
  display: flex; align-items: center; gap: 8px;
  font-size: 10px; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.09em; color: #71717a;
  margin-bottom: 10px;
}
.fa-tile-value {
  font-size: 24px; font-weight: 800; color: #fafafa;
  letter-spacing: -0.02em;
}
.fa-accent-violet .fa-tile-head svg { color: #a78bfa; }
.fa-accent-violet { border-color: rgba(139,92,246,0.18); background: linear-gradient(135deg, rgba(139,92,246,0.08), rgba(24,24,27,0.6)); }
.fa-accent-green .fa-tile-head svg { color: #34d399; }
.fa-accent-green { border-color: rgba(16,185,129,0.18); background: linear-gradient(135deg, rgba(16,185,129,0.08), rgba(24,24,27,0.6)); }
.fa-accent-cyan .fa-tile-head svg { color: #67e8f9; }
.fa-accent-cyan { border-color: rgba(6,182,212,0.18); background: linear-gradient(135deg, rgba(6,182,212,0.08), rgba(24,24,27,0.6)); }
.fa-accent-amber .fa-tile-head svg { color: #fbbf24; }
.fa-accent-amber { border-color: rgba(245,158,11,0.2); background: linear-gradient(135deg, rgba(245,158,11,0.08), rgba(24,24,27,0.6)); }

/* Section */
.fa-section {
  margin-bottom: 26px;
  padding: 18px 18px 20px;
  border-radius: 14px;
  background: rgba(15,15,18,0.9);
  border: 1px solid rgba(255,255,255,0.05);
}
.fa-section-title {
  margin: 0 0 12px; font-size: 13px; font-weight: 800;
  color: #fafafa; text-transform: uppercase; letter-spacing: 0.06em;
}
.fa-section-hint {
  margin: -8px 0 14px; font-size: 12px; color: #71717a;
}

.fa-split {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 14px;
}

/* Top pills rows */
.fa-table { display: flex; flex-direction: column; gap: 6px; }

/* Hot pills (last 7 days) — compact, orange accent */
.fa-hot-list { display: flex; flex-direction: column; gap: 6px; }
.fa-hot-row {
  display: grid;
  grid-template-columns: 28px 1fr auto;
  gap: 12px; align-items: center;
  padding: 10px 12px; border-radius: 10px;
  background: rgba(251,146,60,0.04);
  border: 1px solid rgba(251,146,60,0.14);
  transition: background 0.15s, border-color 0.15s;
}
.fa-hot-row:hover {
  background: rgba(251,146,60,0.07);
  border-color: rgba(251,146,60,0.25);
}
.fa-hot-rank {
  font-size: 14px; font-weight: 800; color: #fb923c;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.fa-hot-body { min-width: 0; }
.fa-hot-text {
  font-size: 13px; color: #fafafa; font-weight: 600;
  line-height: 1.4; margin-bottom: 5px;
}
.fa-hot-meta { display: flex; flex-wrap: wrap; gap: 5px; }
.fa-hot-count {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 99px;
  background: rgba(251,146,60,0.1);
  border: 1px solid rgba(251,146,60,0.3);
  color: #fdba74;
  font-size: 12px; font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.fa-hot-count svg { color: #fb923c; }

.fa-row {
  display: grid;
  grid-template-columns: 42px 1fr 48px;
  gap: 12px; align-items: center;
  padding: 10px 12px; border-radius: 10px;
  background: rgba(24,24,27,0.5);
  border: 1px solid rgba(255,255,255,0.04);
}
.fa-row-rank {
  font-size: 11px; font-weight: 800; color: #71717a;
  text-align: center;
}
.fa-row-body { min-width: 0; }
.fa-row-text {
  font-size: 13px; color: #e4e4e7; font-weight: 600;
  margin-bottom: 6px; line-height: 1.4;
}
.fa-row-meta {
  display: flex; flex-wrap: wrap; gap: 5px;
  margin-bottom: 8px;
}
.fa-row-count {
  font-size: 16px; font-weight: 800; color: #a78bfa;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.fa-bar {
  height: 4px; border-radius: 99px;
  background: rgba(255,255,255,0.04); overflow: hidden;
}
.fa-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, rgba(139,92,246,0.6), rgba(167,139,250,0.9));
  border-radius: 99px;
  transition: width 0.25s ease;
}

/* Chips */
.fa-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 4px;
  font-size: 10px; font-weight: 700;
  background: rgba(139,92,246,0.08);
  border: 1px solid rgba(139,92,246,0.18);
  color: #c4b5fd;
  font-family: inherit;
}
.fa-chip-id {
  background: rgba(255,255,255,0.03);
  border-color: rgba(255,255,255,0.06);
  color: #71717a;
  font-family: 'SF Mono', 'Consolas', monospace;
}
.fa-chip-time {
  background: rgba(6,182,212,0.06);
  border-color: rgba(6,182,212,0.14);
  color: #67e8f9;
}

/* Group tables */
.fa-group { display: flex; flex-direction: column; gap: 6px; }
.fa-group-row {
  display: grid;
  grid-template-columns: 160px 1fr 50px;
  gap: 12px; align-items: center;
  padding: 8px 10px; border-radius: 8px;
  background: rgba(24,24,27,0.5);
  border: 1px solid rgba(255,255,255,0.04);
  font-size: 12px;
}
.fa-group-label { color: #e4e4e7; font-weight: 600; }
.fa-group-bar {
  height: 6px; border-radius: 99px;
  background: rgba(255,255,255,0.04); overflow: hidden;
}
.fa-group-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, rgba(6,182,212,0.6), rgba(103,232,249,0.9));
  border-radius: 99px;
}
.fa-group-count {
  font-weight: 800; color: #67e8f9; text-align: right;
  font-variant-numeric: tabular-nums;
}

/* Dead pills */
.fa-dead-search {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; border-radius: 8px;
  background: rgba(24,24,27,0.5);
  border: 1px solid rgba(255,255,255,0.06);
  margin-bottom: 10px;
}
.fa-dead-search svg { color: #71717a; flex-shrink: 0; }
.fa-dead-search input {
  flex: 1; background: none; border: none; outline: none;
  color: #e4e4e7; font-size: 13px;
  font-family: inherit;
}
.fa-dead-search input::placeholder { color: #52525b; }
.fa-dead-list { display: flex; flex-direction: column; gap: 6px; }
.fa-dead-row {
  padding: 10px 12px; border-radius: 10px;
  background: rgba(245,158,11,0.03);
  border: 1px solid rgba(245,158,11,0.1);
}
.fa-dead-text {
  font-size: 13px; color: #fef3c7; font-weight: 600;
  margin-bottom: 6px;
}
.fa-dead-meta { display: flex; flex-wrap: wrap; gap: 5px; }

/* Recent list */
.fa-recent { display: flex; flex-direction: column; gap: 6px; }
.fa-recent-row {
  display: grid; grid-template-columns: 170px 1fr;
  gap: 12px; align-items: start;
  padding: 10px 12px; border-radius: 10px;
  background: rgba(24,24,27,0.4);
  border: 1px solid rgba(255,255,255,0.04);
  font-size: 12px;
}
.fa-recent-time {
  color: #71717a; font-family: 'SF Mono', monospace;
  font-size: 11px;
}
.fa-recent-text { color: #e4e4e7; margin-bottom: 6px; font-weight: 500; }
.fa-recent-meta { display: flex; flex-wrap: wrap; gap: 5px; }

.fa-empty {
  padding: 18px; text-align: center;
  font-size: 13px; color: #71717a; font-style: italic;
  border: 1px dashed rgba(255,255,255,0.06);
  border-radius: 10px;
}

@media (max-width: 640px) {
  .fa-row { grid-template-columns: 28px 1fr 40px; gap: 8px; }
  .fa-group-row { grid-template-columns: 100px 1fr 40px; }
  .fa-recent-row { grid-template-columns: 1fr; }
}
`;
